import { User } from "oidc-client-ts";
import { QueryObserverOptions, useQuery } from "@tanstack/react-query";
import { doDelete, doGet, doPost, doPut } from "./util/apiHelper.ts";
import {
  useAuthenticatedMutation,
  useAuthenticatedQuery,
} from "./util/queryHelper.ts";
import { useAuth } from "react-oidc-context";
import {
  InstrumentWithFilters,
  InstrumentWithFiltersAndTranslations,
} from "./instrumentApi.ts";
import { LOCATION_QUERY_KEYS } from "./util/queryKeys.ts";

export type LocationType = "IN_PERSON" | "BOTH" | "REMOTE";

export interface LocationSearchResponse {
  locations: Location[];
}

export type LocationInstrumentsWithFiltersRequest = Omit<
  InstrumentWithFilters,
  "translations"
>;

export interface LocationResponse {
  locations: LocationWithName[];
}

export interface Location {
  id: string;
  longitude?: number;
  latitude?: number;
  countryCode?: string;
  postalCode?: string;
  city?: string;
  streetName?: string;
  houseNumber?: string;
  region?: string;
  buildingName?: string;
  subBuildingName?: string;
  timeZone: string;
  locationType: LocationType;
}

export type LocationWithName = Location & {
  name: string;
  instruments: InstrumentWithFiltersAndTranslations[];
};

export type LocationWithNameRequest = Location & {
  name: string;
  instruments: LocationInstrumentsWithFiltersRequest[];
};

export interface LocationRequest {
  longitude?: number;
  latitude?: number;
  countryCode?: string;
  postalCode?: string;
  city?: string;
  streetName?: string;
  houseNumber?: string;
  region?: string;
  buildingName?: string;
  subBuildingName?: string;
  name: string;
  timeZone: string;
  instruments: LocationInstrumentsWithFiltersRequest[];
  locationType: LocationType;
}

export interface UpdateLocationRequest {
  locationId: string;
  locationData: Partial<LocationWithNameRequest>;
}

async function searchLocations(
  query: string,
  user?: User | null,
): Promise<Location[]> {
  return (
    await doGet<LocationSearchResponse>(`/public/locations/search`, user, {
      query: query,
    })
  ).locations;
}

async function searchLocationsByCoordinates(
  longitude: number,
  latitude: number,
  user?: User | null,
): Promise<Location[]> {
  return (
    await doGet<LocationSearchResponse>(
      `/public/locations/search/coordinates`,
      user,
      {
        longitude: longitude,
        latitude: latitude,
      },
    )
  ).locations;
}

async function getSingleLocation(
  user: User,
  locationId: string,
  userId?: string,
): Promise<LocationWithName> {
  return (await getLocations(user, userId)).find(
    (location) => location.id === locationId,
  )!;
}

async function getLocations(
  user: User,
  userId: string | undefined,
): Promise<LocationWithName[]> {
  return (
    await doGet<LocationResponse>(
      `/users/${userId ? userId : user.profile.sub}/locations`,
      user,
    )
  ).locations;
}

async function postLocation(location: LocationRequest, user: User) {
  return await doPost(`/users/${user.profile.sub}/locations`, user, location);
}

async function updateLocation(
  locationId: string,
  locationData: Partial<LocationWithNameRequest>,
  user: User,
) {
  return await doPut(
    `/users/${user.profile.sub}/locations/${locationId}`,
    user,
    {
      // Send all possible fields that might be updated
      name: locationData.name,
      instruments: locationData.instruments,
      locationType: locationData.locationType,
      longitude: locationData.longitude,
      latitude: locationData.latitude,
      countryCode: locationData.countryCode,
      postalCode: locationData.postalCode,
      city: locationData.city,
      streetName: locationData.streetName,
      houseNumber: locationData.houseNumber,
      region: locationData.region,
      buildingName: locationData.buildingName,
      subBuildingName: locationData.subBuildingName,
      timeZone: locationData.timeZone,
    },
  );
}

async function deleteLocation(id: string, user: User) {
  return await doDelete(`/users/${user.profile.sub}/locations/${id}`, user);
}

export function useLocationsQuery(userId?: string) {
  return useAuthenticatedQuery<LocationWithName[]>(
    [...LOCATION_QUERY_KEYS.locations, userId],
    async (user) => {
      return await getLocations(user, userId);
    },
  );
}

export function useLocationQuery(
  locationId: string,
  userId?: string,
  options?: Omit<
    QueryObserverOptions<LocationWithName>,
    "queryKey" | "queryFn"
  >,
) {
  return useAuthenticatedQuery(
    [...LOCATION_QUERY_KEYS.location, locationId],
    (user) => {
      return getSingleLocation(user, locationId, userId);
    },
    { ...options },
  );
}

export function useLocationMutation() {
  return useAuthenticatedMutation(
    (location: LocationRequest, user) => postLocation(location, user),
    {
      invalidateKeys: [
        LOCATION_QUERY_KEYS.locations,
        LOCATION_QUERY_KEYS.teacherLocations,
      ],
    },
  );
}

export function useUpdateLocationMutation() {
  return useAuthenticatedMutation(
    ({ locationId, locationData }: UpdateLocationRequest, user) =>
      updateLocation(locationId, locationData, user),
    {
      invalidateKeys: [
        LOCATION_QUERY_KEYS.locations,
        LOCATION_QUERY_KEYS.location,
        LOCATION_QUERY_KEYS.teacherLocations,
      ],
    },
  );
}

export function useDeleteLocationMutation() {
  return useAuthenticatedMutation(
    (locationId: string, user) => deleteLocation(locationId, user),
    {
      invalidateKeys: [
        LOCATION_QUERY_KEYS.locations,
        LOCATION_QUERY_KEYS.teacherLocations,
      ],
    },
  );
}

export function useLocationSearchQuery(query?: string) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["locationSearch", query],
    queryFn: async () => {
      if (!query) {
        return [];
      }
      return await searchLocations(query, user);
    },

    enabled: !!query,
    refetchOnMount: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export function useLocationSearchByCoordinatesQuery(
  longitude?: number,
  latitude?: number,
) {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["locationSearchByCoordinates", longitude, latitude],
    queryFn: async () => {
      if (!longitude || !latitude) {
        return [];
      }
      return await searchLocationsByCoordinates(longitude, latitude, user);
    },
    enabled: !!longitude && !!latitude,
    refetchOnMount: false,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
